import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useActiveCanvas, useAutoUpdateRef, useCanvasId } from "@/utils/utils";
import {
  GLOBAL_DEV_MODE,
  useIframePoolStore,
} from "@/components/Canvas/IframePool/iframePoolStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  handleErrorFromIframe,
  useIframePostMessaging,
} from "./useIframePostMessaging";
import { ApplicationStoryboardIframe } from "./ApplicationStoryboardIframe";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useDevServerLaunched } from "@/utils/devServerUtils";
import { useIframePoolUrl } from "../hooks/useStoryboardUrl";
import { useTelemetryStore } from "@/store/telemetryStore";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import { useViewportCenterDebounced } from "../stores/canvasStore";

export const IframePool: React.FC = () => {
  const DEV_MODE = GLOBAL_DEV_MODE || false;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const updateStoryArgValues = useCodebaseStore(
    (store) => store.updateStoryArgValues,
  );

  const canvasId = useCanvasId();
  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  // Get tempobook iframe from store
  const tempobookIframe = useIframePoolStore((state) => state.tempobookIframe);
  const initializeIframe = useIframePoolStore(
    (store) => store.initializeIframe,
  );
  const setIframeContentWindow = useIframePoolStore(
    (store) => store.setIframeContentWindow,
  );

  const activeCanvas = useActiveCanvas();
  const activeCanvasRef = useAutoUpdateRef(activeCanvas);

  // Get viewport center in canvas coordinates for positioning the iframe
  const viewportCenter = useViewportCenterDebounced();

  // Use the iframe post messaging hook
  const { onLoadIFrame } = useIframePostMessaging();

  // Get saved storyboard HTML from DB and sync it to the iframe pool
  const savedStoryboardHtml = useSupabaseStore(
    (store) => store.savedStoryboardHtml,
  );
  const savedStoryboardHtmlStatus = useSupabaseStore(
    (store) => store.savedStoryboardHtmlStatus,
  );
  const setSavedStoryboardHtml = useIframePoolStore(
    (store) => store.setSavedStoryboardHtml,
  );

  // Initialize iframe when canvas changes
  useEffect(() => {
    initializeIframe(canvasId);
  }, [canvasId]);

  // Sync DB data to iframe pool when it loads
  useEffect(() => {
    if (savedStoryboardHtmlStatus === "succeeded") {
      setSavedStoryboardHtml(savedStoryboardHtml);
    }
  }, [savedStoryboardHtmlStatus]);

  const scriptReadyRef = useRef(false);
  const iframeLoadedRef = useRef(false);

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress,
  );
  const projectLoadingProgressRef = useAutoUpdateRef(projectLoadingProgress);

  const devServerLaunched = useDevServerLaunched();
  const devServerLaunchedRef = useAutoUpdateRef(devServerLaunched);

  // Handle iframe load
  const handleLoad = async () => {
    if (!iframeRef.current?.contentWindow) {
      console.error({
        msg: "[TEMPO DEBUG] Iframe failed to load - no content window",
      });
      useIframePoolStore.getState().markIframeFailedToLoad();
      useIframePoolStore.getState().resetIframe(true);
      return;
    }

    iframeLoadedRef.current = true;

    if (!scriptReadyRef.current) {
      // Wait for the script to be ready, the script ready message will trigger the onLoadIFrame call
      return;
    }

    // Call onLoadIFrame which handles all the standard setup
    await onLoadIFrame(iframeRef.current.contentWindow);

    // Update the iframe pool store with the content window
    setIframeContentWindow(iframeRef.current.contentWindow);
  };

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === "NOTIFY_HOT_RELOAD_TS") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        useIframePoolStore
          .getState()
          .handleIframeHotReloadTs(event.data.payload.hotReloadTs);
      } else if (event.data.type === "UPDATED_STORY_ARGS") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        const { storyArgs, fileKey } = event.data.payload;
        const matchingStoryboard = Object.values(
          useIframePoolStore.getState().registeredStoryboards,
        ).find((sb) => sb.fileKey === fileKey);
        if (!matchingStoryboard) {
          return;
        }

        const storyboardId = matchingStoryboard.id;
        updateStoryArgValues(storyboardId, storyArgs);
      } else if (event.data.type === "SCRIPT_READY") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        // Update the project loading progress if it needs to be set
        if (
          projectLoadingProgressRef.current?.devtools_script_responsive !==
            true &&
          devServerLaunchedRef.current
        ) {
          await useSupabaseStore
            .getState()
            .supabaseClient?.from("project_loading_progress")
            .update({
              devtools_script_responsive: true,
              last_updated: new Date().toISOString(),
            })
            .eq("canvas_id", canvasId);

          useDatabaseSyncWrapperStore
            .getState()
            .triggerRefetchProjectLoadingProgress();
        }

        scriptReadyRef.current = true;

        // Track script ready for telemetry
        const currentIframe = useIframePoolStore.getState().tempobookIframe;
        if (currentIframe?.id) {
          useTelemetryStore.getState().markScriptReady({
            canvasId,
            iframeId: currentIframe.id,
          });
        }

        // If the script gets reset, we need to call onLoadIFrame again
        if (iframeRef.current?.contentWindow && iframeLoadedRef.current) {
          await onLoadIFrame(iframeRef.current.contentWindow);
          setIframeContentWindow(iframeRef.current.contentWindow);
        }
      } else if (event.data.type === "ERROR") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        if (event.data.error) {
          // Add to all storyboards that are not application storyboards and are currently visible
          handleErrorFromIframe(
            event.data.error,
            activeCanvasRef.current?.storyboards
              .filter(
                (sb) =>
                  sb.type !== STORYBOARD_TYPES.APPLICATION &&
                  !sb.deleted &&
                  !sb.renderStatus,
              )
              .map((sb) => sb.id),
            event.data.isConsoleError,
          );
        }
      } else if (event.data.type === "HEARTBEAT") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        // Handle heartbeat from the tempobook iframe (no argument = main iframe)
        useIframePoolStore.getState().handleHeartbeatReceived();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [canvasId]);

  // Handle iframe error and retry logic
  const handleError = () => {
    console.error({
      msg: "[TEMPO DEBUG] Iframe failed to load - error",
    });
    useIframePoolStore.getState().markIframeFailedToLoad();
    useIframePoolStore.getState().resetIframe(true);
  };

  useEffect(() => {
    if (devServerLaunched) {
      useIframePoolStore.getState().resetIframe(true);
    }
  }, [devServerLaunched]);

  // Build tempobook URL
  const tempobookUrl = useIframePoolUrl();

  // Get application storyboards
  const applicationStoryboards = useIframePoolStore(
    (state) => state.applicationStoryboards,
  );

  if (!devServerUrl) {
    return null;
  }

  // Use drive position if set, otherwise use default style
  const drivePosition = tempobookIframe?.drivePosition;
  const iframeStyle = drivePosition
    ? {
        position: "fixed" as const,

        // Add 1px padding to the iframe to avoid border issues
        left: `${drivePosition.left - 1}px`,
        top: `${drivePosition.top - 1}px`,
        width: `${drivePosition.width + 2}px`,
        height: `${drivePosition.height + 2}px`,
        visibility: "visible" as const,
        pointerEvents: drivePosition.visible
          ? ("auto" as const)
          : ("none" as const),
        zIndex: 1000,
        border: "none",
        opacity: 1,
      }
    : {
        // Position iframe at viewport center (in canvas coordinates), centered on the 1200x800 iframe
        left: DEV_MODE ? 0 : viewportCenter.x - 600,
        top: DEV_MODE ? 1000 : viewportCenter.y - 400,
        position: "fixed" as const,
        zIndex: 1000,
        width: DEV_MODE ? 1200 : 1200,
        height: DEV_MODE ? 5000 : 800,
        opacity: DEV_MODE ? 1 : 0.001,
        pointerEvents: "none" as const,
        border: "none",
      };

  return (
    <>
      {DEV_MODE && tempobookIframe && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 1000 - 30,
            width: 500,
          }}
        >
          {tempobookIframe.id}
        </div>
      )}
      {tempobookIframe && (
        <iframe
          ref={iframeRef}
          id={tempobookIframe.id}
          key={`${tempobookIframe.id}-${tempobookIframe.refreshKey || 0}`}
          src={tempobookUrl}
          style={iframeStyle}
          onLoad={handleLoad}
          onError={handleError}
          className="bg-secondary-200 tempo-component-storyboard-iframe"
        />
      )}
      {Object.keys(applicationStoryboards).map((storyboardId) => (
        <ApplicationStoryboardIframe
          key={storyboardId}
          storyboardId={storyboardId}
        />
      ))}
    </>
  );
};
